import { api } from '@/lib/api/client';
import type { Device, DeviceFilters, DevicesResponse } from '../types';

const DEVICES_URL = '/devices';

export const devicesApi = {
  // Get all devices with filters
  getAll: async (filters?: DeviceFilters): Promise<DevicesResponse> => {
    const params: Record<string, unknown> = { ...filters };

    if (params['page'] !== undefined && params['limit']) {
      params['offset'] = (params['page'] as number) * (params['limit'] as number);
      delete params['page'];
    }

    // Handle search - only send if 2+ chars
    if (params['s'] && (params['s'] as string).length < 2) {
      delete params['s'];
    }

    // Handle date filters
    if (params['createdAt.min']) {
      const minDate = new Date(params['createdAt.min'] as string);
      minDate.setHours(0, 0, 0, 0);
      params['createdAt.min'] = minDate.toISOString();
    }

    if (params['createdAt.max']) {
      const maxDate = new Date(params['createdAt.max'] as string);
      maxDate.setHours(23, 59, 59, 999);
      params['createdAt.max'] = maxDate.toISOString();
    }

    // Remove empty values
    Object.keys(params).forEach((key) => {
      if (params[key] == null || params[key] === '') {
        delete params[key];
      }
    });

    // Remove order params if empty
    if (!params['orderBy'] || !params['order']) {
      delete params['orderBy'];
      delete params['order'];
    }

    return api.get<DevicesResponse>(DEVICES_URL, { params });
  },

  // Get single device by ID
  getById: async (id: number | string): Promise<Device> => {
    return api.get<Device>(`${DEVICES_URL}/${id}`);
  },

  // Delete device
  delete: async (id: number | string): Promise<void> => {
    return api.delete(`${DEVICES_URL}/${id}`);
  },

  // Activate device
  activate: async (id: number | string): Promise<Device> => {
    return api.patch<Device>(`${DEVICES_URL}/${id}/activate`);
  },

  // Block device
  block: async (id: number | string): Promise<Device> => {
    return api.patch<Device>(`${DEVICES_URL}/${id}/block`);
  },
};

// Query keys for React Query
export const devicesQueryKeys = {
  all: ['devices'] as const,
  lists: () => [...devicesQueryKeys.all, 'list'] as const,
  list: (filters?: DeviceFilters) => [...devicesQueryKeys.lists(), filters] as const,
  details: () => [...devicesQueryKeys.all, 'detail'] as const,
  detail: (id: number | string) => [...devicesQueryKeys.details(), id] as const,
};
